// BHP-1083: domain availability check + registration on Backend2.0.
// Replaces the legacy `POST /api/v1/domain/check` (react-bootstrap) with
// `GET /api/domain-availability?name=<sld>&tlds[]=...` (Tailwind). The user
// searches a second-level label; we query the default TLD set and let them
// register an available domain (RegisterDomainModal → POST /api/domains).
import { useState } from 'react';
import {
  type DomainAvailability,
  formatAmount,
  tldsForTerm,
  toSecondLevelLabel
} from '../../features/domain/domain';
import { useDomainAvailability } from '../../features/domain/application/domainAvailability';
import RegisterDomainModal from '../../features/domain/ui/RegisterDomainModal';
import { toUserMessage } from '../../shared/http/errorMessage';

/**
 * BHP-1127: what registering costs, and what it costs after that.
 *
 * Most TLDs register cheaper than they renew (.it: 4,99 against 15,99), and a
 * single unlabelled amount was the first-year price — so a customer could click
 * Register on a number that stops applying after a year. When the two amounts
 * are the same there is nothing to contrast, and one line says it better.
 */
function Prices({ result }: { result: DomainAvailability }): JSX.Element {
  const registration = formatAmount(result.priceCurrency, result.priceAmount);
  const renewal = formatAmount(result.priceCurrency, result.renewalPriceAmount);

  if (registration !== null && registration === renewal) {
    return (
      <span className="text-sm text-slate-600">{registration} / year</span>
    );
  }

  return (
    <span className="flex flex-col items-end leading-tight">
      <span className="text-sm text-slate-600">
        {registration === null
          ? 'Price unavailable'
          : `${registration} first year`}
      </span>
      <span className="text-xs text-slate-400">
        {renewal === null ? 'renewal price unknown' : `then ${renewal} / year`}
      </span>
    </span>
  );
}

function ResultRow({
  result,
  onRegister
}: {
  result: DomainAvailability;
  onRegister: (result: DomainAvailability) => void;
}): JSX.Element {
  // Premium domains are available at the registrar but the backend refuses to
  // register them without manual approval (DomainProcessor), so offering
  // "Register" here would be a guaranteed dead end.
  const canRegister = result.available && !result.premium;

  return (
    <li className="flex items-center justify-between gap-3 border-0 border-b border-solid border-slate-100 px-4 py-3 last:border-b-0">
      <span className="font-medium text-slate-800">{result.domain}</span>
      <span className="flex items-center gap-4">
        {!result.available ? (
          <span className="text-sm text-slate-400">Taken</span>
        ) : (
          <>
            <Prices result={result} />
            {canRegister ? (
              <button
                type="button"
                onClick={() => {
                  onRegister(result);
                }}
                className="rounded-md border-0 bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700"
              >
                Register
              </button>
            ) : (
              <span
                title="Premium domains require manual approval — please contact support."
                className="rounded bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"
              >
                Premium — on request
              </span>
            )}
          </>
        )}
      </span>
    </li>
  );
}

/**
 * BHP-1128: an extension our catalogue does not carry. Before, the backend's 422
 * made the row disappear without a word, so a refused extension was
 * indistinguishable from a broken check. No amount and no Register button: there
 * is nothing to buy here.
 */
function UnsupportedRow({
  sld,
  tld
}: {
  sld: string;
  tld: string;
}): JSX.Element {
  return (
    <li className="flex items-center justify-between gap-3 border-0 border-b border-solid border-slate-100 px-4 py-3 last:border-b-0">
      <span className="font-medium text-slate-800">
        {sld}.{tld}
      </span>
      <span className="text-sm text-slate-400">We don’t offer .{tld}</span>
    </li>
  );
}

export default function CheckDomain(): JSX.Element {
  const [term, setTerm] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [selected, setSelected] = useState<DomainAvailability | null>(null);

  const sld = toSecondLevelLabel(submitted);
  const { data, isFetching, isError, error } = useDomainAvailability(
    sld,
    tldsForTerm(submitted),
    sld.length > 0
  );

  const onSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setSubmitted(term);
  };

  return (
    <div className="mx-auto max-w-2xl">
      <h2 className="mb-1 text-lg font-semibold text-slate-900">
        Is your domain available?
      </h2>
      {/* Follows what is in the box, not what was last searched: it says what
          pressing Check will do, including an extension typed just now. */}
      <p className="mb-4 text-sm text-slate-500">
        We check the following extensions:{' '}
        {tldsForTerm(term)
          .map((tld) => `.${tld}`)
          .join(' ')}
      </p>

      <form onSubmit={onSubmit} className="flex gap-2">
        <input
          type="search"
          value={term}
          onChange={(event) => {
            setTerm(event.currentTarget.value);
          }}
          placeholder="Search for a domain…"
          aria-label="Domain name"
          className="flex-1 rounded-md border border-solid border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-teal-500 focus:outline-none"
        />
        <button
          type="submit"
          className="rounded-md border-0 bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
        >
          Check
        </button>
      </form>

      <div className="mt-6">
        {sld.length === 0 ? null : isFetching ? (
          <p className="text-sm text-slate-500">Checking availability…</p>
        ) : isError ? (
          <p
            role="alert"
            className="rounded-lg border border-solid border-rose-200 bg-rose-50 p-4 text-sm text-rose-700"
          >
            {toUserMessage(
              error,
              'Could not check availability. Please try again.'
            )}
          </p>
        ) : data !== undefined ? (
          <ul className="overflow-hidden rounded-xl border border-solid border-slate-200 bg-white">
            {/* Refused extensions head the list: the only way one ends up in
                the request is the customer typing it, and tldsForTerm puts a
                typed extension first for that same reason (BHP-1122). */}
            {data.unsupportedTlds.map((tld) => (
              <UnsupportedRow key={tld} sld={sld} tld={tld} />
            ))}
            {data.results.map((result) => (
              <ResultRow
                key={result.domain}
                result={result}
                onRegister={setSelected}
              />
            ))}
          </ul>
        ) : null}
      </div>

      {selected !== null ? (
        <RegisterDomainModal
          sld={sld}
          availability={selected}
          onClose={() => {
            setSelected(null);
          }}
        />
      ) : null}
    </div>
  );
}
